class CollectionProducts {
  constructor() {
    this.firstFetchDone = false;
    this.activeButton = null;
    this.pendingFilterTag = null;
    this.init();
  }

  // initialization: binding of event handlers
  init() {
    this.bindLinkClicks();
    this.bindButtonClicks();
    this.bindClearButtonClicks();
    this.bindPaginationLinks();
    this.openSavedAccordion();

    this.bindSortSelects();
  }

  // binding event handlers for links
  bindLinkClicks() {
    document.body.addEventListener("click", (e) => {
      const linkElement = e.target.closest(
        ".filter-collections__tag-wrapper a",
      );
      if (!linkElement) return;

      const parentContent = linkElement.closest(".filter-collections__content");
      if (parentContent) {
        const previousActivePoint = parentContent.querySelector(
          ".filter-collections__filter-point.active",
        );

        // remove active from all and put it on the current one
        parentContent
          .querySelectorAll(".filter-collections__filter-point")
          .forEach((point) => point.classList.remove("active"));
        const currentPoint = linkElement.closest(
          ".filter-collections__filter-point",
        );
        if (currentPoint) currentPoint.classList.add("active");

        if (previousActivePoint) {
          const previousLinkElement = previousActivePoint.querySelector(
            ".filter-collections__tag-wrapper a",
          );
          const previousTitleAttr =
            previousLinkElement?.getAttribute("title") || "";
          const previousTag = getTagFromTitle(previousTitleAttr);

          if (previousTag) {
            linkElement.href = removeTagFromUrl(linkElement.href, previousTag);
          }
        }
      }

      this.handleLinkClick(e, linkElement);
    });

    // helpers
    function getTagFromTitle(titleAttr) {
      const match = String(titleAttr).match(/\btag\s+([A-Za-z0-9:_\-]+)/i);
      return match ? match[1] : null;
    }

    function removeTagFromUrl(urlString, tagToRemove) {
      const siteOrigin = location.origin;
      const urlObject = new URL(urlString, siteOrigin);

      const pathSegments = urlObject.pathname.split("/").filter(Boolean);
      const indexOfCollections = pathSegments.indexOf("collections");
      const indexOfTags = indexOfCollections >= 0 ? indexOfCollections + 2 : -1;

      if (indexOfTags >= 0 && pathSegments[indexOfTags]) {
        const tagSegments = pathSegments[indexOfTags]
          .split("+")
          .filter(Boolean);
        const filteredTags = tagSegments.filter(
          (tag) => decodeURIComponent(tag) !== tagToRemove,
        );

        if (filteredTags.length) {
          pathSegments[indexOfTags] = filteredTags.join("+");
        } else {
          pathSegments.splice(indexOfTags, 1);
        }
        urlObject.pathname = "/" + pathSegments.join("/");
      }

      return urlObject.pathname + urlObject.search + urlObject.hash;
    }
  }

  handleLinkClick(event, link) {
    event.preventDefault();

    this.pendingFilterTag =
      link.dataset.tag || String(link.getAttribute("title") || "").replace(/^tag\s+/i, "");

    const currentUrl = new URL(window.location.href);
    const newUrl = new URL(link.href);

    const sortBy = currentUrl.searchParams.get('sort_by');
    if (sortBy && !newUrl.searchParams.get('sort_by')) {
      newUrl.searchParams.set('sort_by', sortBy);
    }

    const finalUrl = newUrl.toString();
    history.pushState(null, null, finalUrl);
    this.fetchSectionRender(finalUrl, { scrollToGridOnMobile: true });
  }

  // bind event handlers for buttons
  bindButtonClicks() {
    document.querySelectorAll("[data-button]").forEach((button) => {
      button.setAttribute("aria-expanded", button.classList.contains("active") ? "true" : "false");
    });

    document.body.addEventListener("click", (e) => {
      const button = e.target.closest("[data-button]");
      if (button) {
        this.handleButtonClick(e, button);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.activeButton) return;

      const activeTables = Array.from(
        document.querySelectorAll(`[data-table="${this.activeButton}"]`),
      );
      const activeButtons = Array.from(
        document.querySelectorAll(`[data-button="${this.activeButton}"]`),
      );
      const focusIsInActiveFilter = activeTables.some((table) =>
        table.contains(document.activeElement),
      );
      const focusIsOnActiveButton = activeButtons.includes(document.activeElement);
      if (!focusIsInActiveFilter && !focusIsOnActiveButton) return;

      event.preventDefault();
      event.stopPropagation();
      const buttonToFocus = activeButtons.find((button) => button.getClientRects().length) || activeButtons[0];
      this.closeFilterCollection(this.activeButton);
      buttonToFocus?.focus();
    }, true);
  }

  closeFilterCollection(blockId) {
    document.querySelectorAll(`[data-button="${blockId}"]`).forEach((button) => {
      button.classList.remove("active");
      button.setAttribute("aria-expanded", "false");
    });
    document.querySelectorAll(`[data-table="${blockId}"]`).forEach((table) => {
      table.style.display = "none";
    });
    this.activeButton = null;
  }

  handleButtonClick(event, button) {
    const blockId = button.getAttribute("data-button");
    const targetTables = document.querySelectorAll(`[data-table="${blockId}"]`);

    if (button.classList.contains("active")) {
      this.closeFilterCollection(blockId);
      return;
    }

    const allButtons = document.querySelectorAll("[data-button]");
    allButtons.forEach((btn) => {
      btn.classList.remove("active");
      btn.setAttribute("aria-expanded", "false");
    });

    targetTables.forEach((table) => {
      table.style.display = "flex";
    });

    const buttonsWithSameId = document.querySelectorAll(
      `[data-button="${blockId}"]`,
    );
    buttonsWithSameId.forEach((btn) => {
      btn.classList.add("active");
      btn.setAttribute("aria-expanded", "true");
    });

    this.activeButton = blockId;

    const allTables = document.querySelectorAll("[data-table]");
    allTables.forEach((table) => {
      if (!table.matches(`[data-table="${blockId}"]`)) {
        table.style.display = "none";
      }
    });

    if (event.detail === 0) {
      requestAnimationFrame(() => {
        const firstFilterLink = Array.from(targetTables)
          .flatMap((table) => Array.from(table.querySelectorAll(".filter-collections__filter-point a[href]")))
          .find((link) => link.getClientRects().length);
        firstFilterLink?.focus();
      });
    }
  }

  // bind event handlers for cleanup buttons
  bindClearButtonClicks() {
    document.body.addEventListener("click", (e) => {
      const button = e.target.closest(".js-clear-button");
      if (button) {
        e.preventDefault();
        this.handleClearButtonClick(e, button);
      }
    });
  }

  handleClearButtonClick(event, button) {
    event.preventDefault();
    const link = button.getAttribute("data-link");

    if (!link) return;

    const newUrl = new URL(link, location.origin);

    newUrl.searchParams.delete('sort_by');

    const finalUrl = newUrl.toString();

    if (window.location.href !== finalUrl) {
      history.pushState(null, null, finalUrl);
      this.fetchSectionRender(finalUrl, { scrollToGridOnMobile: true });
    }

    document.querySelectorAll('[data-sort]').forEach((block) => {
      const label = block.querySelector('[data-current]');
      const allOptions = block.querySelectorAll('[data-value]');
      allOptions.forEach((li) => li.classList.remove('active'));

      const firstItem = block.querySelector('[data-value="best-selling"]');
      if (firstItem) {
        firstItem.classList.add('active');
        label.textContent = firstItem.textContent;
      }
    });
  }

  // binding event handlers for pagination
  bindPaginationLinks() {
    document.body.addEventListener("click", (e) => {
      const link = e.target.closest(".collection-pagination a");
      if (link) {
        this.handlePaginationLinkClick(e, link);
      }
    });
  }

  handlePaginationLinkClick(event, link) {
    event.preventDefault();
    const url = link.href;
    history.pushState(null, null, url);
    this.fetchSectionRender(url);

    const filterButtonContainer = document.querySelector(
      ".filter-collections__wrapper",
    );
    if (filterButtonContainer) {
      filterButtonContainer.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  // opening a saved accordion
  openSavedAccordion() {
    if (this.activeButton) {
      const savedButtons = document.querySelectorAll(
        `[data-button="${this.activeButton}"]`,
      );
      if (savedButtons.length > 0) {
        savedButtons.forEach((savedButton) => {
          savedButton.classList.add("active");
          savedButton.setAttribute("aria-expanded", "true");
        });

        const targetTables = document.querySelectorAll(
          `[data-table="${this.activeButton}"]`,
        );
        targetTables.forEach((table) => {
          table.style.display = "flex";
        });
      }
    }
  }

  // fetch to load the section
  fetchSectionRender(url, options = {}) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Error on page loading");
        }
        return response.text();
      })
      .then((responseText) => this.handleFetchSuccess(responseText, options))
      .catch((error) => {
        console.error(error);
      });
  }

  // processing of successful fetch, insertion into DOM
  handleFetchSuccess(responseText, options = {}) {
    const doc = new DOMParser().parseFromString(responseText, "text/html");

    const currentFilterCollectionsWrapper = document.querySelector(
      ".filter-collections__tag-wrapper",
    );
    const newFilterCollectionsWrapper = doc.querySelector(
      ".filter-collections__tag-wrapper",
    );
    if (currentFilterCollectionsWrapper && newFilterCollectionsWrapper) {
      currentFilterCollectionsWrapper.innerHTML =
        newFilterCollectionsWrapper.innerHTML;
    }

    const newCollectionProducts = doc.querySelector("collection-products");
    const currentCollectionProducts = document.querySelector(
      "collection-products",
    );
    if (newCollectionProducts && currentCollectionProducts) {
      currentCollectionProducts.replaceWith(newCollectionProducts);
    }

    if (options.scrollToGridOnMobile) {
      this.scrollToCollectionGrid();
    }

    this.firstFetchDone = true;
    this.openSavedAccordion();
    this.restoreFilterTagFocus();
  }

  restoreFilterTagFocus() {
    if (!this.pendingFilterTag) return;

    const tag = this.pendingFilterTag;
    this.pendingFilterTag = null;

    requestAnimationFrame(() => {
      const matchingLink = Array.from(
        document.querySelectorAll(".filter-collections__filter-point a[data-tag]"),
      ).find((link) => link.dataset.tag === tag && link.getClientRects().length);
      matchingLink?.focus();
    });
  }

  scrollToCollectionGrid() {
    if (!window.matchMedia("(max-width: 767px)").matches) {
      return;
    }

    requestAnimationFrame(() => {
      const collectionGrid = document.querySelector(".collection-pgrid");
      if (!collectionGrid) return;

      collectionGrid.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  //sort_by function
  bindSortSelects() {
    const sortBlocks = document.querySelectorAll('[data-sort]');
    if (!sortBlocks.length) return;

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const focusedBlock = document.activeElement?.closest?.('[data-sort]');
      const openBlock = focusedBlock?.classList.contains('open')
        ? focusedBlock
        : document.querySelector('[data-sort].open');
      if (!openBlock) return;

      event.preventDefault();
      event.stopPropagation();
      openBlock.classList.remove('open');
      openBlock.querySelectorAll('[data-value]').forEach((item) =>
        item.setAttribute('tabindex', '-1')
      );
      const openButton = openBlock.querySelector('.sort-select__button');
      openButton?.setAttribute('aria-expanded', 'false');
      openButton?.focus();
    }, true);

    sortBlocks.forEach((block) => {
      const button = block.querySelector('.sort-select__button');
      const list = block.querySelector('.sort-select__list');
      const label = block.querySelector('[data-current]');
      const items = block.querySelectorAll('[data-value]');

      const close = (restoreFocus = false) => {
        block.classList.remove('open');
        button.setAttribute('aria-expanded', 'false');
        items.forEach((item) => item.setAttribute('tabindex', '-1'));
        if (restoreFocus) button.focus();
      };

      const open = (focusOption = true) => {
        block.classList.add('open');
        button.setAttribute('aria-expanded', 'true');
        items.forEach((item) => {
          item.setAttribute('role', 'option');
          item.setAttribute('tabindex', '0');
        });
        if (focusOption) {
          const activeItem = block.querySelector('[data-value].active') || items[0];
          activeItem?.focus();
        }
      };

      const focusItem = (index) => {
        const normalizedIndex = (index + items.length) % items.length;
        items[normalizedIndex].focus();
      };

      button.addEventListener('click', () => {
        if (block.classList.contains('open')) close();
        else open();
      });

      button.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        open(false);
        const activeIndex = Array.from(items).findIndex((item) => item.classList.contains('active'));
        focusItem(event.key === 'ArrowUp' ? activeIndex - 1 : Math.max(activeIndex, 0));
      });

      items.forEach((item) => {
        item.addEventListener('click', () => {
          const value = item.dataset.value;

          document.querySelectorAll('[data-sort]').forEach((b) => {
            b.querySelectorAll('[data-value]').forEach((li) =>
              li.classList.toggle('active', li.dataset.value === value)
            );
            b.querySelectorAll('[data-value]').forEach((li) =>
              li.setAttribute('aria-selected', li.dataset.value === value ? 'true' : 'false')
            );
            b.querySelector('[data-current]').textContent = item.textContent;
            b.classList.remove('open');
            b.querySelector('.sort-select__button')?.setAttribute('aria-expanded', 'false');
            b.querySelectorAll('[data-value]').forEach((li) => li.setAttribute('tabindex', '-1'));
          });

          this.applySort(value);
        });

        item.addEventListener('keydown', (event) => {
          const itemIndex = Array.from(items).indexOf(item);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusItem(itemIndex + 1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            focusItem(itemIndex - 1);
          } else if (event.key === 'Home') {
            event.preventDefault();
            focusItem(0);
          } else if (event.key === 'End') {
            event.preventDefault();
            focusItem(items.length - 1);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            item.click();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
          }
        });
      });

      block.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!block.contains(document.activeElement)) close();
        });
      });

      close();
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-sort]')) {
        document.querySelectorAll('[data-sort]').forEach((b) => {
          b.classList.remove('open');
          b.querySelector('.sort-select__button')?.setAttribute('aria-expanded', 'false');
          b.querySelectorAll('[data-value]').forEach((li) => li.setAttribute('tabindex', '-1'));
        });
      }
    });

    const currentUrl = new URL(window.location.href);
    const sortBy = currentUrl.searchParams.get('sort_by') || 'best-selling';
    sortBlocks.forEach((block) => {
      const label = block.querySelector('[data-current]');
      const activeItem = block.querySelector(`[data-value="${sortBy}"]`);
      if (activeItem) {
        label.textContent = activeItem.textContent;
        block
          .querySelectorAll('[data-value]')
          .forEach((li) =>
            li.classList.toggle('active', li.dataset.value === sortBy)
          );
      }
    });
  }

  applySort(value) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);

    history.pushState(null, null, url.toString());
    this.fetchSectionRender(url.toString());
  }

}

document.addEventListener("DOMContentLoaded", () => new CollectionProducts());
